import { calculateGoodItemsTotalWeight } from "helpers/quotationFormHelper";

import type {
  InvitedProviderCard,
  MapQuoteDetailsOptions,
  ShipmentDetailsSheetViewModel,
} from "./types";

type QuoteDetails = {
  organization?: Record<string, unknown>;
  service?: Record<string, unknown>;
  schedule?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  quick_request_details?: Array<Record<string, unknown>>;
  shipment_id?: string;
};

const mapProviderCards = (cards: unknown[]): InvitedProviderCard[] =>
  cards.map((card) => {
    const c = card as Record<string, unknown>;
    return {
      id: c.id as number,
      companyName: c.company_name as string,
      companyLogo: (c.company_logo as string | null) ?? null,
      teamName: (c.team_name as string | null) ?? null,
      parcelAccountName: (c.parcel_account_name as string | null) ?? null,
      parcelAccountNumber: (c.parcel_account_number as string | null) ?? null,
      won: Boolean(c.won),
    };
  });

const mapSpecialRequirements = (json: Record<string, unknown> | undefined) => {
  if (!json) return [];
  return Object.entries(json).map(([key, entry]) => {
    const req = entry as Record<string, unknown>;
    return {
      key,
      label: String(req.label ?? ""),
      value: Boolean(req.value),
      ...(req.additional_information ? { additionalInformation: String(req.additional_information) } : {}),
    };
  });
};

export const buildScheduleStops = (schedule: Record<string, unknown> | undefined): unknown[] => {
  const stops = schedule?.stops;
  if (Array.isArray(stops) && stops.length > 0) {
    return stops;
  }

  if (!schedule) {
    return [];
  }

  const addressV2 = schedule.address_v2 as Record<string, unknown> | undefined;

  return [
    {
      stop_type: "Pickup",
      address_name: addressV2?.pickup_name ?? schedule.pickup_address_name,
      street: addressV2?.pickup_street_address ?? schedule.pickup_street,
      street_line_two: addressV2?.pickup_street_address_line_two ?? schedule.pickup_street_line_two,
      city: addressV2?.pickup_city ?? schedule.pickup_city,
      state: addressV2?.pickup_state ?? schedule.pickup_state,
      country: addressV2?.pickup_country ?? schedule.pickup_country,
      zipcode: addressV2?.pickup_zipcode ?? schedule.pickup_zipcode,
      contact_name: schedule.pickup_contact_name,
      contact_phone: schedule.pickup_contact_phone,
      contact_email: schedule.pickup_contact_email,
      datetime: schedule.pickup_datetime,
      end_datetime: schedule.pickup_end_datetime,
      hours_start: schedule.pickup_hours_start,
      hours_end: schedule.pickup_hours_end,
      notes: schedule.pickup_notes,
      address_type: schedule.pickup_address_type,
      time_type: schedule.pickup_time_type,
      airport: schedule.pickup_airport,
      airport_code: schedule.pickup_airport_code,
      seaport: schedule.pickup_seaport,
      seaport_code: schedule.pickup_seaport_code,
      is_port_address: addressV2?.pickup_is_port_address,
      un_locode: addressV2?.pickup_un_locode,
      location_reference_number: addressV2?.pickup_location_reference_number,
      custom_address_reference: addressV2?.pickup_custom_address_reference,
    },
    {
      stop_type: "Delivery",
      address_name: addressV2?.delivery_name ?? schedule.stop1_address_name,
      street: addressV2?.delivery_street_address ?? schedule.stop1_street,
      street_line_two: addressV2?.delivery_street_address_line_two ?? schedule.stop1_street_line_two,
      city: addressV2?.delivery_city ?? schedule.stop1_city,
      state: addressV2?.delivery_state ?? schedule.stop1_state,
      country: addressV2?.delivery_country ?? schedule.stop1_country,
      zipcode: addressV2?.delivery_zipcode ?? schedule.stop1_zipcode,
      contact_name: schedule.stop1_contact_name,
      contact_phone: schedule.stop1_contact_phone,
      contact_email: schedule.stop1_contact_email,
      datetime: schedule.stop1_datetime,
      end_datetime: schedule.stop1_end_datetime,
      hours_start: schedule.stop1_hours_start,
      hours_end: schedule.stop1_hours_end,
      notes: schedule.stop1_notes,
      address_type: schedule.stop1_address_type,
      time_type: schedule.stop1_time_type,
      airport: schedule.stop1_airport,
      airport_code: schedule.stop1_airport_code,
      seaport: schedule.stop1_seaport,
      seaport_code: schedule.stop1_seaport_code,
      is_port_address: addressV2?.delivery_is_port_address,
      un_locode: addressV2?.delivery_un_locode,
      location_reference_number: addressV2?.delivery_location_reference_number,
      custom_address_reference: addressV2?.delivery_custom_address_reference,
    },
  ];
};

export const buildPackingTotals = (
  goodItems: unknown[],
  organization: Record<string, unknown> | undefined,
) => {
  const items = goodItems as Array<Record<string, unknown>>;
  let accumulateCBM = 0;
  let accumulatePieces = 0;

  items.forEach((item) => {
    accumulatePieces += Number(item.units) || 0;
    accumulateCBM += Number(item.total_cbm) || 0;
  });

  const totalWeight = calculateGoodItemsTotalWeight(items);
  const requestedCbm = organization?.requested_cbm as number | undefined;
  const totalCBMValue = requestedCbm ?? accumulateCBM;

  return {
    totalPackages: accumulatePieces,
    totalWeight: Number.isNaN(totalWeight.weight) ? null : totalWeight,
    totalCBM: `${parseFloat(String(totalCBMValue)).toFixed(3)} m³`,
  };
};

const buildOrganisationFields = (
  organization: Record<string, unknown> | undefined,
  quickRequestDetails: Array<Record<string, unknown>> | undefined,
): Record<string, string | null | undefined> => {
  const quickRequest = quickRequestDetails?.[0];
  const quickRequestor = quickRequest?.quick_requestor as Record<string, unknown> | undefined;

  const fields: Record<string, string | null | undefined> = {
    requestor: organization?.requestor as string | undefined,
    requestor_email: organization?.requestor_email as string | undefined,
    request_creation_datetime: organization?.request_creation_datetime as string | undefined,
    invoice_location: organization?.invoice_location as string | undefined,
    cost_center: organization?.cost_center as string | undefined,
    reason_code_group: organization?.reason_code_group as string | undefined,
    reason_code: organization?.reason_code as string | undefined,
    custom_field1: organization?.custom_field1 as string | undefined,
    custom_field2: organization?.custom_field2 as string | undefined,
    custom_field3: organization?.custom_field3 as string | undefined,
    custom_cost_recovery_field1: organization?.custom_cost_recovery_field1 as string | undefined,
    custom_cost_recovery_field2: organization?.custom_cost_recovery_field2 as string | undefined,
    quick_request_reference: quickRequest?.quick_request_reference as string | undefined,
    initial_requestor_name: quickRequestor?.name as string | undefined,
    initial_requestor_email: quickRequestor?.email as string | undefined,
  };

  if (organization?.cost_recovery_required !== undefined && organization?.cost_recovery_required !== null) {
    fields.cost_recovery_required = organization.cost_recovery_required ? "Yes" : "No";
  }

  return fields;
};

export const mapQuoteDetailsToSheet = (
  details: QuoteDetails,
  options: MapQuoteDetailsOptions,
): ShipmentDetailsSheetViewModel => {
  const { organization, schedule, service = {} } = details;

  const {
    export_clearance,
    export_clearance_instructions,
    transit_clearance,
    transit_clearance_instructions,
    import_clearance,
    import_clearance_instructions,
    invited_freight_modes,
    special_requirements_json,
    invited_provider_cards,
    ...serviceRest
  } = service;

  const showProviders =
    (options.userType === "shipper" || options.userType === "staff") &&
    !options.isCustomBroker &&
    Array.isArray(invited_provider_cards) &&
    invited_provider_cards.length > 0;

  const goodsConfirmationType = organization?.goods_confirmation_type as string | undefined;

  return {
    organisation: {
      title: (organization?.title as string) ?? "",
      shipperName: (organization?.shipper as string) ?? "",
      ...((organization?.shipper_logo as string)
        ? { shipperLogo: organization.shipper_logo as string }
        : {}),
      paymentLocation: (organization?.payment_location as string) ?? "",
      paymentLocationReference: organization?.payment_location_reference as string | undefined,
      hideInPrint: Boolean(details.settings?.hide_organizational_data),
      additionalComments: (organization?.additional_comments as string | null) ?? null,
      longField: (organization?.custom_longfield_01 as string | null) ?? null,
      fields: buildOrganisationFields(organization, details.quick_request_details),
    },
    service: {
      ...serviceRest,
      freightModes: Array.isArray(invited_freight_modes) ? (invited_freight_modes as string[]) : [],
      specialRequirements: mapSpecialRequirements(special_requirements_json as Record<string, unknown> | undefined),
    },
    providers: showProviders ? mapProviderCards(invited_provider_cards as unknown[]) : null,
    schedule: {
      stops: buildScheduleStops(schedule),
      ...(schedule?.multistop_instructions
        ? { multistopInstructions: String(schedule.multistop_instructions) }
        : {}),
      ...(schedule?.consignee !== undefined ? { consignee: schedule.consignee } : {}),
      ...(schedule?.notify_party !== undefined ? { notifyParty: schedule.notify_party } : {}),
      ...(typeof schedule?.mileage === "string" && schedule.mileage
        ? { mileage: schedule.mileage }
        : {}),
      clearance: {
        export: export_clearance as string | undefined,
        exportInstructions: export_clearance_instructions as string | undefined,
        transit: transit_clearance as string | undefined,
        transitInstructions: transit_clearance_instructions as string | undefined,
        import: import_clearance as string | undefined,
        importInstructions: import_clearance_instructions as string | undefined,
      },
    },
    packing: {
      goodItems: Array.isArray(organization?.good_items) ? (organization.good_items as unknown[]) : [],
      totals: buildPackingTotals(
        Array.isArray(organization?.good_items) ? (organization.good_items as unknown[]) : [],
        organization,
      ),
      goodsEstimated: goodsConfirmationType === "Estimated",
      goodsConfirmationType: goodsConfirmationType ?? null,
      requestedCbm: (organization?.requested_cbm as number | null | undefined) ?? null,
    },
    shipmentId: options.shipmentId ?? details.shipment_id ?? "",
  };
};
